import myRequest from '@/utils/myRequest';
import tokenService from '@/utils/tokenService';
import SparkMD5 from 'spark-md5';
import { loadJsmediatags } from '@/utils/lazyScript';

/**
 * OneDrive API 服务
 * 使用统一的请求实例，支持自动token刷新和错误处理
 */

// Token缓存机制
let uploadTokenCache = {
  token: null,
  expiresAt: null,
  isRefreshing: false
};

// 规范化 OneDrive 目标目录，去掉开头/结尾的斜杠，避免 root:// 或末尾多斜杠
function normalizeFolderPath(path, fallback = '') {
  let p = (path || fallback || '').toString().trim();
  // 去掉开头的斜杠
  p = p.replace(/^\/+/, '');
  // 去掉结尾的斜杠
  p = p.replace(/\/+$/, '');
  return p;
}

export const oneDriveAPI = {
  /**
   * 获取OneDrive授权URL
   */
  async getAuthUrl() {
    try {
      console.log('🔗 [OneDrive API] 获取授权URL...');
      const response = await myRequest.get('/api/onedrive/auth-url');
      console.log('✅ [OneDrive API] 获取授权URL成功');
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 获取OneDrive授权URL失败:', error);
      throw error;
    }
  },

  /**
   * 检查OneDrive连接状态
   */
  async checkConnection() {
    try {
      console.log('🔍 [OneDrive API] 检查连接状态...');
      const response = await myRequest.get('/api/onedrive/status');
      console.log('✅ [OneDrive API] 连接状态检查成功:', response);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 检查OneDrive连接状态失败:', error);
      
      // 如果是401错误，说明用户token失效，需要重新登录
      if (error.response && error.response.status === 401) {
        console.log('🔐 [OneDrive API] 检测到token失效，需要重新登录');
        throw new Error('TOKEN_EXPIRED');
      }
      
      // 如果是业务错误（如NOT_LOGIN），也抛出特定错误
      if (error.response && error.response.data && error.response.data.code === 'NOT_LOGIN') {
        console.log('🔐 [OneDrive API] 检测到未登录状态');
        throw new Error('NOT_LOGIN');
      }
      
      throw error;
    }
  },

  /**
   * 获取OneDrive存储容量信息
   * @returns {Promise<{total: number, used: number, remaining: number}>} 容量信息（单位：字节）
   */
  async getStorageQuota() {
    try {
      console.log('💾 [OneDrive API] 获取存储容量信息...');
      const response = await myRequest.get('/api/onedrive/storage-quota');
      console.log('✅ [OneDrive API] 获取存储容量信息成功:', response);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 获取存储容量信息失败:', error);
      
      // 如果是401错误，说明用户token失效，需要重新登录
      if (error.response && error.response.status === 401) {
        console.log('🔐 [OneDrive API] 检测到token失效，需要重新登录');
        throw new Error('TOKEN_EXPIRED');
      }
      
      // 如果是业务错误（如NOT_LOGIN），也抛出特定错误
      if (error.response && error.response.data && error.response.data.code === 'NOT_LOGIN') {
        console.log('🔐 [OneDrive API] 检测到未登录状态');
        throw new Error('NOT_LOGIN');
      }
      
      throw error;
    }
  },

  /**
   * 上传文件到OneDrive
   */
  async uploadFile(file, folderPath = '', onProgress = null) {
    try {
      console.log('📤 [OneDrive API] 开始上传文件:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      if (folderPath) {
        formData.append('folderPath', folderPath);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      // 添加上传进度监听
      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        };
      }

      const response = await myRequest.post('/api/onedrive/upload', formData, config);
      console.log('✅ [OneDrive API] 文件上传成功:', file.name);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 上传文件到OneDrive失败:', error);
      
      // 处理token失效的情况
      if (error.response && error.response.status === 401) {
        throw new Error('TOKEN_EXPIRED');
      }
      
      throw error;
    }
  },

  /**
   * 批量上传文件到OneDrive
   */
  async uploadFiles(files, folderPath = '', onProgress = null) {
    const results = [];
    const totalFiles = files.length;
    let completedFiles = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await this.uploadFile(file, folderPath, (fileProgress) => {
          if (onProgress) {
            // 计算总体进度
            const baseProgress = Math.round((completedFiles / totalFiles) * 100);
            const fileProgressContribution = Math.round((fileProgress / totalFiles));
            const totalProgress = Math.min(baseProgress + fileProgressContribution, 100);
            onProgress(totalProgress, file.name, i + 1, totalFiles);
          }
        });
        
        results.push({
          success: true,
          file: file.name,
          result: result
        });
      } catch (error) {
        results.push({
          success: false,
          file: file.name,
          error: error.message
        });
      }
      
      completedFiles++;
    }

    return results;
  },

  /**
   * 上传音乐文件到OneDrive
   * @param {File} file 音乐文件
   * @param {Object} metadata 元数据
   * @param {Object} authorInfo 作者信息（可选）🆕
   * @param {Function} onProgress 进度回调
   */
  async uploadMusic(file, metadata = {}, authorInfo = null, onProgress = null) {
    try {
      console.log('🎵 [OneDrive API] 开始上传音乐文件:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      
      // 添加音乐元数据
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.artist) formData.append('artist', metadata.artist);
      if (metadata.album) formData.append('album', metadata.album);
      if (metadata.releaseYear) formData.append('releaseYear', metadata.releaseYear);
      if (metadata.genre) formData.append('genre', metadata.genre);
      if (metadata.lyrics) formData.append('lyrics', metadata.lyrics);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.duration) formData.append('duration', metadata.duration);
      if (metadata.cover) formData.append('cover', metadata.cover);
      
      // 🆕 添加作者信息
      if (authorInfo) {
        if (authorInfo.nickname) {
          formData.append('authorNickname', authorInfo.nickname);
        }
        if (authorInfo.avatarBlob) {
          // 将Blob转换为File
          const avatarFile = new File(
            [authorInfo.avatarBlob], 
            'author_avatar.jpg', 
            { type: 'image/jpeg' }
          );
          formData.append('authorAvatarFile', avatarFile);
        }
        if (authorInfo.homeUrl) {
          formData.append('authorHomeUrl', authorInfo.homeUrl);
        }
        if (authorInfo.shareUrl) {
          formData.append('shareUrl', authorInfo.shareUrl);
        }
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      // 添加上传进度监听
      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        };
      }

      const response = await myRequest.post('/api/onedrive/music/upload', formData, config);
      console.log('✅ [OneDrive API] 音乐文件上传成功:', file.name);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 上传音乐文件到OneDrive失败:', error);
      
      // 处理token失效的情况
      if (error.response && error.response.status === 401) {
        throw new Error('TOKEN_EXPIRED');
      }
      
      throw error;
    }
  },

  /**
   * 使用预获取的授权上传音乐文件到OneDrive - 批量上传优化版本
   * 🚀 性能优化：复用已获取的上传授权，避免重复调用getUploadAuth
   */
  async uploadMusicWithAuth(file, uploadAuth, metadata = {}, onProgress = null) {
    try {
      console.log('🎵 [OneDrive API] 使用预授权上传音乐文件:', file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      
      // 添加音乐元数据
      if (metadata.title) formData.append('title', metadata.title);
      if (metadata.artist) formData.append('artist', metadata.artist);
      if (metadata.album) formData.append('album', metadata.album);
      if (metadata.releaseYear) formData.append('releaseYear', metadata.releaseYear);
      if (metadata.genre) formData.append('genre', metadata.genre);
      if (metadata.lyrics) formData.append('lyrics', metadata.lyrics);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.duration) formData.append('duration', metadata.duration);
      if (metadata.cover) formData.append('cover', metadata.cover);

      // 添加上传授权信息
      if (uploadAuth && uploadAuth.uploadUrl) {
        formData.append('uploadUrl', uploadAuth.uploadUrl);
      }
      if (uploadAuth && uploadAuth.accessToken) {
        formData.append('accessToken', uploadAuth.accessToken);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };

      // 添加上传进度监听
      if (onProgress) {
        config.onUploadProgress = (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const progress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            onProgress(progress);
          }
        };
      }

      const response = await myRequest.post('/api/onedrive/music/upload', formData, config);
      console.log('✅ [OneDrive API] 音乐文件上传成功:', file.name);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 使用预授权上传音乐文件失败:', error);
      
      // 处理token失效的情况
      if (error.response && error.response.status === 401) {
        throw new Error('TOKEN_EXPIRED');
      }
      
      throw error;
    }
  },

  /**
   * 批量上传音乐文件到OneDrive - 优化版本
   * 🚀 性能优化：批量获取上传授权，避免重复调用后端API
   */
  async uploadMusicFiles(files, onProgress = null) {
    try {
      console.log('🎵 [OneDrive API] 开始批量上传音乐文件:', files.length, '个文件');
      
      const results = [];
      const totalFiles = files.length;
      let completedFiles = 0;

      // 🚀 优化：批量获取上传授权（只调用一次）
      let uploadAuth = null;
      try {
        uploadAuth = await this.getUploadAuth('audio');
        console.log('✅ [OneDrive API] 音乐文件批量上传授权获取成功');
      } catch (error) {
        console.error('❌ [OneDrive API] 音乐文件批量上传授权获取失败:', error);
        // 如果批量授权失败，回退到逐个上传
        console.log('🔄 [OneDrive API] 回退到逐个上传模式');
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            // 提取音乐元数据
            const metadata = await this.extractMusicMetadata(file);
            
            // 不传递文件级别的进度回调，只按文件完成情况更新进度
            const result = await this.uploadMusic(file, metadata, null);
            
            results.push({
              success: true,
              file: file.name,
              result: result
            });
          } catch (error) {
            results.push({
              success: false,
              file: file.name,
              error: error.message
            });
          }
          
          completedFiles++;
          
          // 按文件个数更新进度
          if (onProgress) {
            const progress = Math.round((completedFiles / totalFiles) * 100);
            onProgress(progress, file.name, completedFiles, totalFiles);
          }
        }
        return results;
      }

      // 使用批量授权上传音乐文件
      if (uploadAuth) {
        console.log('🚀 [OneDrive API] 使用批量授权上传音乐文件');
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            // 提取音乐元数据
            const metadata = await this.extractMusicMetadata(file);
            
            // 使用批量授权上传（复用token），不传递文件级别的进度回调
            const result = await this.uploadMusicWithAuth(file, uploadAuth, metadata, null);
            
            results.push({
              success: true,
              file: file.name,
              result: result
            });
            console.log(`✅ [OneDrive API] 音乐文件上传成功: ${file.name}`);
          } catch (error) {
            console.error(`❌ [OneDrive API] 音乐文件上传失败: ${file.name}`, error);
            results.push({
              success: false,
              file: file.name,
              error: error.message
            });
          }
          
          completedFiles++;
          
          // 按文件个数更新进度
          if (onProgress) {
            const progress = Math.round((completedFiles / totalFiles) * 100);
            onProgress(progress, file.name, completedFiles, totalFiles);
          }
        }
      }

      console.log('🎉 [OneDrive API] 批量音乐文件上传完成:', results.length, '个文件');
      return results;
      
    } catch (error) {
      console.error('❌ [OneDrive API] 批量上传音乐文件失败:', error);
      throw error;
    }
  },

  /**
   * 提取音乐文件的元数据
   */
  async extractMusicMetadata(file) {
    const jsmediatagsReady = await loadJsmediatags();
    return new Promise((resolve) => {
      try {
        // 检查文件大小，避免处理过大的文件
        if (file.size > 100 * 1024 * 1024) { // 100MB
          console.warn('文件过大，跳过元数据提取:', file.name, '大小:', file.size);
          resolve({});
          return;
        }
        
        // 检查文件类型
        if (!file.type.startsWith('audio/') && !file.name.toLowerCase().match(/\.(mp3|wav|flac|aac|ogg|m4a)$/)) {
          console.warn('不支持的文件类型，跳过元数据提取:', file.name, '类型:', file.type);
          resolve({});
          return;
        }
        
        // 使用jsmediatags库提取元数据
        if (jsmediatagsReady && window.jsmediatags) {
          // 设置超时机制，防止无限递归
          const timeoutId = setTimeout(() => {
            console.warn('元数据提取超时，跳过:', file.name);
            resolve({});
          }, 10000); // 10秒超时
          
          window.jsmediatags.read(file, {
            onSuccess: function(tag) {
              clearTimeout(timeoutId);
              
              try {
                const metadata = {};
                
                // 安全地提取元数据，避免访问undefined属性
                if (tag && tag.tags) {
                  if (tag.tags.title) metadata.title = tag.tags.title;
                  if (tag.tags.artist) metadata.artist = tag.tags.artist;
                  if (tag.tags.album) metadata.album = tag.tags.album;
                  if (tag.tags.year) metadata.releaseYear = tag.tags.year;
                  if (tag.tags.genre) metadata.genre = tag.tags.genre;
                  if (tag.tags.lyrics) metadata.lyrics = tag.tags.lyrics;
                  
                  // 安全地提取封面图片
                  if (tag.tags.picture && tag.tags.picture.data && Array.isArray(tag.tags.picture.data)) {
                    try {
                      const picture = tag.tags.picture;
                      const mimeType = picture.format || 'image/jpeg';
                      const coverBlob = new Blob([new Uint8Array(picture.data)], { type: mimeType });
                      metadata.cover = coverBlob;
                    } catch (coverError) {
                      console.warn('封面图片处理失败:', coverError);
                    }
                  }
                }
                
                console.log('音乐元数据提取成功:', file.name, metadata);
                resolve(metadata);
              } catch (parseError) {
                console.warn('解析元数据时发生错误:', parseError);
                resolve({});
              }
            },
            onError: function(error) {
              clearTimeout(timeoutId);
              console.warn('提取音乐元数据失败:', file.name, error);
              resolve({});
            }
          });
        } else {
          console.warn('jsmediatags库未加载，无法提取元数据');
          resolve({});
        }
      } catch (error) {
        console.warn('提取音乐元数据时发生错误:', file.name, error);
        resolve({});
      }
    });
  },

  /**
   * 获取OneDrive文件的下载链接
   */
  async getFileDownloadUrl(fileId) {
    try {
      console.log('🔗 [OneDrive API] 获取文件下载链接:', fileId);
      const response = await myRequest.get(`/api/onedrive/file-url/${fileId}`);
      console.log('✅ [OneDrive API] 获取下载链接成功:', response);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 获取下载链接失败:', error);
      throw error;
    }
  },

  /**
   * 批量获取OneDrive文件的下载链接
   * @param {string[]} fileIds - 文件ID数组
   * @returns {Promise<Object>} 返回文件ID到下载链接的映射对象
   */
  async batchGetFileDownloadUrls(fileIds) {
    try {
      if (!fileIds || fileIds.length === 0) {
        console.warn('⚠️ [OneDrive API] 批量获取文件URL: fileIds为空');
        return {};
      }
      
      console.log('🔗 [OneDrive API] 批量获取文件下载链接:', fileIds.length, '个文件');
      const response = await myRequest.post('/api/onedrive/batch-file-urls', {
        fileIds: fileIds
      });
      console.log('✅ [OneDrive API] 批量获取下载链接成功:', Object.keys(response || {}).length, '个成功');
      return response || {};
    } catch (error) {
      console.error('❌ [OneDrive API] 批量获取下载链接失败:', error);
      throw error;
    }
  },

  /**
   * 获取OneDrive文件的流式播放链接
   */
  async getFileStreamUrl(fileId) {
    try {
      console.log('🎵 [OneDrive API] 获取文件流式播放链接:', fileId);
      const response = await myRequest.get(`/api/onedrive/stream-url/${fileId}`);
      console.log('✅ [OneDrive API] 获取流式播放链接成功:', response);
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 获取流式播放链接失败:', error);
      throw error;
    }
  },

  /**
   * 清理upload token缓存
   */
  clearUploadTokenCache() {
    console.log('🧹 [OneDrive API] 清理upload token缓存');
    uploadTokenCache = {
      token: null,
      expiresAt: null,
      isRefreshing: false
    };
  },

  /**
   * 获取OneDrive上传授权 - 用于直连上传（带缓存机制）
   */
  async getUploadAuth(forceRefresh = false) {
    try {
      // 检查缓存是否有效
      const now = Date.now();
      if (!forceRefresh && uploadTokenCache.token && uploadTokenCache.expiresAt && now < uploadTokenCache.expiresAt) {
        console.log('🔑 [OneDrive API] 使用缓存的upload token，剩余有效期:', Math.round((uploadTokenCache.expiresAt - now) / 1000), '秒');
        return {
          accessToken: uploadTokenCache.token,
          expiresIn: Math.round((uploadTokenCache.expiresAt - now) / 1000)
        };
      }
      
      // 如果正在刷新，等待刷新完成
      if (uploadTokenCache.isRefreshing) {
        console.log('🔑 [OneDrive API] 等待其他请求刷新token...');
        while (uploadTokenCache.isRefreshing) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // 刷新完成后再次检查缓存
        if (uploadTokenCache.token && uploadTokenCache.expiresAt && now < uploadTokenCache.expiresAt) {
          console.log('🔑 [OneDrive API] 使用其他请求刷新的token');
          return {
            accessToken: uploadTokenCache.token,
            expiresIn: Math.round((uploadTokenCache.expiresAt - now) / 1000)
          };
        }
      }
      
      // 设置刷新标志
      uploadTokenCache.isRefreshing = true;
      
      console.log('🔑 [OneDrive API] 获取新的上传授权...');
      const response = await myRequest.get('/api/onedrive/upload-auth');
      
      // 更新缓存
      uploadTokenCache.token = response.accessToken;
      uploadTokenCache.expiresAt = now + (response.expiresIn * 1000);
      uploadTokenCache.isRefreshing = false;
      
      console.log('✅ [OneDrive API] 获取上传授权成功，已缓存，有效期:', response.expiresIn, '秒');
      return response;
    } catch (error) {
      uploadTokenCache.isRefreshing = false;
      console.error('❌ [OneDrive API] 获取上传授权失败:', error);
      
      // 处理token失效的情况
      if (error.response && error.response.status === 401) {
        throw new Error('TOKEN_EXPIRED');
      }
      
      throw error;
    }
  },

  /**
   * 直接上传文件到OneDrive - 小文件（< 4MB）
   */
  async uploadSmallFile(file, accessToken, folderPath, onProgress) {
    try {
      console.log('📤 [OneDrive API] 开始小文件直传:', file.name);
      
      const folder = normalizeFolderPath(folderPath, '');
      const fileName = folder ? `${folder}/${file.name}` : file.name;
      const uploadUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/${fileName}:/content`;
      
      // 对于小文件，我们使用XMLHttpRequest来支持进度回调
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // 监听上传进度
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable && onProgress) {
            const progress = Math.round((event.loaded / event.total) * 100);
            onProgress(progress);
            console.log(`📊 [OneDrive API] 小文件上传进度: ${progress}% (${Math.round(event.loaded / 1024)}KB/${Math.round(event.total / 1024)}KB)`);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText);
              console.log('✅ [OneDrive API] 小文件直传成功:', file.name);
              resolve(result);
            } catch (parseError) {
              console.error('❌ [OneDrive API] 解析响应失败:', parseError);
              reject(new Error('响应解析失败'));
            }
          } else {
            reject(new Error(`上传失败: ${xhr.status} ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          reject(new Error('网络错误'));
        });
        
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.setRequestHeader('Content-Type', 'application/octet-stream');
        xhr.send(file);
      });
    } catch (error) {
      console.error('❌ [OneDrive API] 小文件直传失败:', error);
      throw error;
    }
  },

  /**
   * 直接上传文件到OneDrive - 大文件（> 4MB）分片上传
   */
  async uploadLargeFile(file, accessToken, folderPath, onProgress) {
    try {
      console.log('📤 [OneDrive API] 开始大文件分片直传:', file.name);
      
      const fileName = `${folderPath}/${file.name}`;
      
      // 1. 创建上传会话
      const sessionUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/${fileName}:/createUploadSession`;
      const sessionResponse = await fetch(sessionUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          item: {
            "@microsoft.graph.conflictBehavior": "rename"
          }
        })
      });
      
      if (!sessionResponse.ok) {
        throw new Error(`创建上传会话失败: ${sessionResponse.status}`);
      }
      
      const session = await sessionResponse.json();
      
      // 2. 分片上传
      const chunkSize = 4 * 1024 * 1024; // 4MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);
      let lastResponse = null;
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        
        const uploadResponse = await fetch(session.uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Length': chunk.size.toString(),
            'Content-Range': `bytes ${start}-${end-1}/${file.size}`
          },
          body: chunk
        });
        
        if (!uploadResponse.ok) {
          throw new Error(`分片上传失败: ${uploadResponse.status}`);
        }
        
        // 保存最后一个响应
        lastResponse = uploadResponse;
        
        // 更新上传进度
        if (onProgress) {
          const progress = Math.round(((i + 1) / totalChunks) * 100);
          onProgress(progress);
          console.log(`📊 [OneDrive API] 分片上传进度: ${progress}% (${i + 1}/${totalChunks})`);
        }
      }
      
      const result = await lastResponse.json();
      console.log('✅ [OneDrive API] 大文件分片直传成功:', file.name);
      return result;
    } catch (error) {
      console.error('❌ [OneDrive API] 大文件分片直传失败:', error);
      throw error;
    }
  },

  /**
   * 直接上传文件到OneDrive - 自动选择小文件或大文件上传方式
   */
  async uploadFileToOneDrive(file, accessToken, folderPath, onProgress) {
    try {
      console.log('📤 [OneDrive API] 开始文件直传:', file.name, '大小:', file.size);
      
      if (file.size < 4 * 1024 * 1024) {
        // 小文件直接上传
        return await this.uploadSmallFile(file, accessToken, folderPath, onProgress);
      } else {
        // 大文件分片上传
        return await this.uploadLargeFile(file, accessToken, folderPath, onProgress);
      }
    } catch (error) {
      console.error('❌ [OneDrive API] 文件直传失败:', error);
      throw error;
    }
  },

  /**
   * 计算文件MD5值
   */
  async calculateFileMD5(file) {
    return new Promise((resolve, reject) => {
      const spark = new SparkMD5.ArrayBuffer();
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          spark.append(e.target.result);
          const md5 = spark.end();
          resolve(md5);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = (error) => {
        reject(error);
      };
      
      reader.readAsArrayBuffer(file);
    });
  },

  /**
   * 提取音频元数据（时长等）
   */
  async extractAudioMetadata(file) {
    return new Promise((resolve) => {
      const audio = new Audio();
      const url = URL.createObjectURL(file);
      
      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(url);
        resolve({
          duration: Math.round(audio.duration),
          title: null, // 需要专门的音频元数据提取库
          artist: null,
          album: null,
          genre: null,
          year: null
        });
      };
      
      audio.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({
          duration: 0,
          title: null,
          artist: null,
          album: null,
          genre: null,
          year: null
        });
      };
      
      audio.src = url;
    });
  },

  /**
   * 规范化歌词字段，确保始终返回字符串
   * @param {any} lyrics - 歌词内容，可能是字符串或对象
   * @returns {string|null} 规范化后的歌词字符串
   */
  normalizeLyrics(lyrics) {
    if (!lyrics) {
      return null;
    }
    
    // 如果已经是字符串，直接返回
    if (typeof lyrics === 'string') {
      return lyrics.trim() || null;
    }
    
    // 如果是对象（如 { lrc, tlyric, rlyric }），提取 lrc 字段
    if (typeof lyrics === 'object' && lyrics !== null) {
      // 优先使用 lrc 字段
      if (lyrics.lrc && typeof lyrics.lrc === 'string') {
        return lyrics.lrc.trim() || null;
      }
      // 如果没有 lrc，尝试使用 content 字段
      if (lyrics.content && typeof lyrics.content === 'string') {
        return lyrics.content.trim() || null;
      }
      // 如果都没有，返回 null
      return null;
    }
    
    // 其他类型，尝试转换为字符串
    try {
      const str = String(lyrics);
      return str.trim() || null;
    } catch (e) {
      console.warn('⚠️ [OneDrive API] 歌词字段格式异常，无法转换:', lyrics);
      return null;
    }
  },

  /**
   * 完整的OneDrive直传音乐上传流程
   * @param {File} musicFile 音乐文件
   * @param {Object} metadata 元数据
   * @param {File} coverFile 封面文件
   * @param {Object} authorInfo 作者信息（可选）🆕
   * @param {Function} onProgress 进度回调
   */
  async uploadMusicToOneDrive(musicFile, metadata, coverFile, authorInfo, onProgress) {
    try {
      console.log('🎵 [OneDrive API] 开始OneDrive直传音乐上传流程');
      
      // 1. 获取上传授权
      const authResponse = await this.getUploadAuth();
      const accessToken = authResponse.accessToken;
      const musicFolderPath = authResponse.musicFolderPath || 'imusic/music-audio';
      const coverFolderPath = authResponse.coverFolderPath || 'imusic/music-cover';
      
      // 2. 并行计算 MD5 与提取音频元数据
      const [md5, extractedMetadata] = await Promise.all([
        this.calculateFileMD5(musicFile),
        this.extractAudioMetadata(musicFile)
      ]);

      // 3. 准备封面上传文件
      let coverToUpload = coverFile;
      if (!coverToUpload && metadata && metadata.cover) {
        console.log('🖼️ [OneDrive API] 从音频元数据中提取到封面图片');
        coverToUpload = metadata.cover;
        if (coverToUpload instanceof Blob && !(coverToUpload instanceof File)) {
          const coverFileName = `${musicFile.name.replace(/\.[^/.]+$/, '')}_cover.jpg`;
          coverToUpload = new File([coverToUpload], coverFileName, { type: coverToUpload.type || 'image/jpeg' });
        }
      }

      // 4. 并行上传音频、封面、作者头像
      console.log('📤 [OneDrive API] 开始并行上传音频/封面/头像到 OneDrive...');
      const avatarFile = authorInfo?.avatarBlob
        ? new File([authorInfo.avatarBlob], 'author_avatar.jpg', { type: 'image/jpeg' })
        : null;

      const [musicUploadResult, coverUploadResult, avatarUploadResult] = await Promise.all([
        this.uploadFileToOneDrive(musicFile, accessToken, musicFolderPath, onProgress),
        coverToUpload
          ? this.uploadFileToOneDrive(coverToUpload, accessToken, coverFolderPath)
          : Promise.resolve(null),
        avatarFile
          ? this.uploadFileToOneDrive(avatarFile, accessToken, 'imusic/author-avatars').catch((error) => {
              console.warn('⚠️ [OneDrive API] 作者头像上传失败，继续保存音乐:', error);
              return null;
            })
          : Promise.resolve(null)
      ]);

      const authorAvatarFileId = avatarUploadResult?.id || null;
      if (authorAvatarFileId) {
        console.log('✅ [OneDrive API] 作者头像上传成功，fileId:', authorAvatarFileId);
      }
      
      // 6. 准备保存到服务器的数据
      const saveData = {
        musicFile: {
          fileId: musicUploadResult.id,
          fileName: musicFile.name,
          fileSize: musicFile.size,
          md5: md5, // 使用真正的MD5值
          mimeType: musicFile.type,
          duration: extractedMetadata.duration,
          folderPath: musicFolderPath
        },
        metadata: {
          title: metadata.title || extractedMetadata.title,
          artist: metadata.artist || extractedMetadata.artist,
          album: metadata.album || extractedMetadata.album,
          genre: metadata.genre || extractedMetadata.genre,
          releaseDate: metadata.releaseDate,
          lyrics: this.normalizeLyrics(metadata.lyrics)
        },
        coverImage: coverUploadResult ? {
          fileId: coverUploadResult.id,
          fileName: coverToUpload.name || 'cover.jpg',
          fileSize: coverToUpload.size,
          mimeType: coverToUpload.type || 'image/jpeg'
        } : null,
        extractedMetadata: extractedMetadata,
        // 🆕 展开作者信息到顶层字段（后端期望的格式）
        authorNickname: authorInfo?.nickname || null,
        authorAvatarFileId: authorAvatarFileId,  // OneDrive文件ID
        authorHomeUrl: authorInfo?.homeUrl || null,
        shareUrl: authorInfo?.shareUrl || null
      };
      
      // 7. 保存音乐信息到服务器
      console.log('💾 [OneDrive API] 保存音乐信息到服务器...');
      const saveResult = await this.saveMusicInfo(saveData);
      
      console.log('✅ [OneDrive API] OneDrive直传音乐上传完成:', saveResult);
      return saveResult;
      
    } catch (error) {
      console.error('❌ [OneDrive API] OneDrive直传音乐上传失败:', error);
      throw error;
    }
  },

  /**
   * 上传视频到音乐的OneDrive目录（imusic/music-video）
   */
  async uploadVideoToMusicFolder(videoFile, metadata, onProgress, accessToken = null) {
    try {
      console.log('🎬 [OneDrive API] 开始上传视频到音乐目录');
      
      // 1. 获取上传授权（如果未提供）
      let authResponse;
      if (!accessToken) {
        authResponse = await this.getUploadAuth();
        accessToken = authResponse.accessToken;
      } else {
        console.log('🎬 [OneDrive API] 使用提供的accessToken，跳过授权请求');
      }
      
      // 2. 并行计算 MD5 与上传视频/封面
      const musicVideoFolderPath = 'imusic/music-video';
      const md5Promise = this.calculateFileMD5(videoFile);
      console.log('📤 [OneDrive API] 开始并行上传视频与封面到 OneDrive 音乐目录...');

      const [videoUploadResult, coverUploadResult, md5] = await Promise.all([
        this.uploadFileToOneDrive(videoFile, accessToken, musicVideoFolderPath, onProgress),
        metadata.coverFile
          ? this.uploadFileToOneDrive(metadata.coverFile, accessToken, 'imusic/music-cover').catch((coverError) => {
              console.warn('⚠️ [OneDrive API] 视频封面上传失败，继续处理视频:', coverError);
              return null;
            })
          : Promise.resolve(null),
        md5Promise
      ]);

      let coverImageInfo = null;
      if (coverUploadResult && metadata.coverFile) {
        coverImageInfo = {
          fileId: coverUploadResult.id,
          fileName: metadata.coverFile.name,
          fileSize: metadata.coverFile.size,
          mimeType: metadata.coverFile.type || 'image/jpeg'
        };
        console.log('✅ [OneDrive API] 视频封面上传成功:', coverImageInfo);
      }
      
      // 5. 准备保存到服务器的数据
      const saveData = {
        musicFile: {
          fileId: videoUploadResult.id,
          fileName: videoFile.name,
          fileSize: videoFile.size,
          md5: md5,
          mimeType: videoFile.type || 'video/mp4',
          duration: metadata.duration || 0,
          folderPath: musicVideoFolderPath
        },
        metadata: {
          title: metadata.title || videoFile.name.replace(/\.[^/.]+$/, ""),
          artist: metadata.artist || '未知艺术家',
          album: metadata.album || '用户上传的视频',
          genre: metadata.genre || '视频',
          releaseDate: metadata.releaseDate,
          lyrics: this.normalizeLyrics(metadata.lyrics)
        },
        coverImage: coverImageInfo, // 使用上传到OneDrive的封面信息
        extractedMetadata: {
          duration: metadata.duration || 0,
          title: metadata.title,
          resolution: metadata.resolution || '',
          format: metadata.format || 'mp4'
        }
      };
      
      // 6. 保存视频信息到服务器
      console.log('💾 [OneDrive API] 保存视频信息到服务器...');
      const saveResult = await this.saveMusicInfo(saveData);
      
      console.log('✅ [OneDrive API] 视频上传到音乐目录完成:', saveResult);
      return saveResult;
      
    } catch (error) {
      console.error('❌ [OneDrive API] 视频上传到音乐目录失败:', error);
      throw error;
    }
  },

  /**
   * 完整的OneDrive直传视频上传流程
   * @param {File} videoFile 视频文件
   * @param {Object} metadata 元数据
   * @param {File} coverFile 封面文件
   * @param {Object} authorInfo 作者信息（可选）🆕
   * @param {Function} onProgress 进度回调
   */
  async uploadVideoToOneDrive(videoFile, metadata, coverFile, authorInfo, onProgress) {
    try {
      console.log('🎬 [OneDrive API] 开始OneDrive直传视频上传流程');
      
      // 1. 获取上传授权
      const authResponse = await this.getUploadAuth();
      const accessToken = authResponse.accessToken;
      const videoFolderPath = authResponse.videoFolderPath || 'imusic/music-video';
      const coverFolderPath = authResponse.coverFolderPath || 'imusic/music-cover';
      
      // 2. 并行计算 MD5，并同时上传视频/封面/作者头像
      const md5Promise = this.calculateFileMD5(videoFile);
      const avatarFile = authorInfo?.avatarBlob
        ? new File([authorInfo.avatarBlob], 'author_avatar.jpg', { type: 'image/jpeg' })
        : null;

      console.log('📤 [OneDrive API] 开始并行上传视频/封面/头像到 OneDrive...');
      const [videoUploadResult, coverUploadResult, avatarUploadResult, md5] = await Promise.all([
        this.uploadFileToOneDrive(videoFile, accessToken, videoFolderPath, onProgress),
        coverFile
          ? this.uploadFileToOneDrive(coverFile, accessToken, coverFolderPath)
          : Promise.resolve(null),
        avatarFile
          ? this.uploadFileToOneDrive(avatarFile, accessToken, 'imusic/author-avatars').catch((error) => {
              console.warn('⚠️ [OneDrive API] 作者头像上传失败，继续保存视频:', error);
              return null;
            })
          : Promise.resolve(null),
        md5Promise
      ]);

      const authorAvatarFileId = avatarUploadResult?.id || null;
      if (authorAvatarFileId) {
        console.log('✅ [OneDrive API] 作者头像上传成功，fileId:', authorAvatarFileId);
      }
      
      // 5. 准备保存到服务器的数据
      const saveData = {
        musicFile: {
          fileId: videoUploadResult.id,
          fileName: videoFile.name,
          fileSize: videoFile.size,
          md5: md5,
          mimeType: videoFile.type,
          duration: metadata.duration || 0,
          folderPath: videoFolderPath
        },
        metadata: {
          title: metadata.title,
          artist: metadata.artist,
          album: metadata.album || '抖音视频',
          genre: metadata.genre,
          releaseDate: metadata.releaseDate,
          lyrics: this.normalizeLyrics(metadata.lyrics)
        },
        coverImage: coverUploadResult ? {
          fileId: coverUploadResult.id,
          fileName: coverFile.name,
          fileSize: coverFile.size,
          mimeType: coverFile.type
        } : null,
        extractedMetadata: {
          duration: metadata.duration || 0,
          title: metadata.title,
          artist: metadata.artist
        },
        // 🆕 展开作者信息到顶层字段
        authorNickname: authorInfo && authorInfo.nickname ? authorInfo.nickname : null,
        authorAvatarFileId: authorAvatarFileId,
        authorHomeUrl: authorInfo && authorInfo.homeUrl ? authorInfo.homeUrl : null,
        shareUrl: authorInfo && authorInfo.shareUrl ? authorInfo.shareUrl : null
      };
      
      // 6. 保存视频信息到服务器
      console.log('💾 [OneDrive API] 保存视频信息到服务器...');
      const saveResult = await this.saveMusicInfo(saveData);
      
      console.log('✅ [OneDrive API] OneDrive直传视频上传完成:', saveResult);
      return saveResult;
      
    } catch (error) {
      console.error('❌ [OneDrive API] OneDrive直传视频上传失败:', error);
      throw error;
    }
  },


  /**
   * 保存音乐信息到服务器
   */
  async saveMusicInfo(saveData) {
    try {
      console.log('💾 [OneDrive API] 保存音乐信息到服务器...');
      const response = await myRequest.post('/api/onedrive/save-music-info', saveData);
      console.log('✅ [OneDrive API] 音乐信息保存成功');
      return response;
    } catch (error) {
      console.error('❌ [OneDrive API] 保存音乐信息失败:', error);
      throw error;
    }
  },

  /**
   * 从OneDrive URL流式下载文件
   */
  async downloadFileFromUrl(url, onProgress = null) {
    try {
      console.log('📥 [OneDrive API] 开始从URL流式下载文件:', url);
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status} ${response.statusText}`);
      }
      
      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;
      let loaded = 0;
      
      const reader = response.body.getReader();
      const chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        loaded += value.length;
        
        // 更新进度
        if (onProgress && total > 0) {
          const progress = Math.round((loaded / total) * 100);
          onProgress(progress, loaded, total);
        }
      }
      
      // 合并所有chunks为Blob
      const blob = new Blob(chunks);
      console.log('✅ [OneDrive API] 流式下载完成:', {
        size: blob.size,
        type: blob.type || 'application/octet-stream'
      });
      
      return blob;
    } catch (error) {
      console.error('❌ [OneDrive API] 流式下载失败:', error);
      throw error;
    }
  },

  /**
   * 流式传输：从源OneDrive下载并直接上传到目标OneDrive
   * 这是您提到的核心功能 - 完全绕过服务器中转
   */
  async streamTransferFile(sourceUrl, targetAccessToken, targetFolderPath, fileName, onProgress = null) {
    try {
      console.log('🔄 [OneDrive API] 开始流式传输文件:', fileName);
      console.log('📥 源URL:', sourceUrl);
      console.log('📤 目标文件夹:', targetFolderPath);
      
      // 1. 从源URL流式下载文件
      console.log('📥 [流式传输] 开始从源OneDrive下载...');
      const blob = await this.downloadFileFromUrl(sourceUrl, (downloadProgress, downloaded, total) => {
        if (onProgress) {
          // 下载阶段占50%进度
          const progress = Math.round(downloadProgress * 0.5);
          onProgress(progress, `下载中: ${Math.round(downloaded/1024)}KB / ${Math.round(total/1024)}KB`);
        }
      });
      
      // 2. 将Blob转换为File对象
      const file = new File([blob], fileName, { 
        type: blob.type || 'application/octet-stream' 
      });
      
      console.log('📤 [流式传输] 开始上传到目标OneDrive...');
      
      // 3. 流式上传到目标OneDrive
      const uploadResult = await this.uploadFileToOneDrive(
        file, 
        targetAccessToken, 
        targetFolderPath, 
        (uploadProgress) => {
          if (onProgress) {
            // 上传阶段占50%进度，从50%开始
            const progress = 50 + Math.round(uploadProgress * 0.5);
            onProgress(progress, `上传中: ${uploadProgress}%`);
          }
        }
      );
      
      console.log('✅ [OneDrive API] 流式传输完成:', uploadResult);
      return uploadResult;
      
    } catch (error) {
      console.error('❌ [OneDrive API] 流式传输失败:', error);
      throw error;
    }
  },

  /**
   * 计算文件MD5值
   */
  async calculateMD5(buffer) {
    try {
      // 使用SparkMD5库计算MD5，支持ArrayBuffer
      const spark = new SparkMD5.ArrayBuffer();
      spark.append(buffer);
      const hashHex = spark.end();
      return hashHex;
    } catch (error) {
      console.warn('⚠️ MD5计算失败，使用时间戳作为替代:', error);
      // 如果MD5计算失败，使用时间戳作为替代
      return Date.now().toString();
    }
  },

  /**
   * 流式传输视频文件：从分享链接下载并保存到用户OneDrive
   * 这是专门为视频音乐分享设计的功能
   */
  async streamTransferVideo(sourceUrl, musicMetadata = {}, onProgress = null) {
    try {
      console.log('🎬 [OneDrive API] 开始流式传输视频文件');
      
      // 1. 获取目标用户的上传授权
      const authResponse = await this.getUploadAuth();
      const accessToken = authResponse.accessToken;
      const videoFolderPath = authResponse.videoFolderPath || 'imusic/music-video';
      
      // 2. 生成唯一的文件名（视频格式）
      let fileName;
      
      // 优先使用音乐元数据生成文件名
      if (musicMetadata.title) {
        console.log('🎬 [文件名清理] 原始标题:', musicMetadata.title);
        console.log('🎬 [文件名清理] 原始艺术家:', musicMetadata.artist);
        
        // 清理文件名中的非法字符（包括更多特殊字符）
        const cleanTitle = musicMetadata.title
          .replace(/[<>:"/\\|?*#%&+=\s]+/g, '_')  // 替换特殊字符为下划线
          .replace(/[^\w\-_.]/g, '')              // 只保留字母、数字、下划线、连字符、点
          .replace(/_+/g, '_')                    // 合并多个下划线
          .replace(/^_|_$/g, '')                  // 移除开头和结尾的下划线
          .trim();
        
        const cleanArtist = musicMetadata.artist ? musicMetadata.artist
          .replace(/[<>:"/\\|?*#%&+=\s]+/g, '_')  // 替换特殊字符为下划线
          .replace(/[^\w\-_.]/g, '')              // 只保留字母、数字、下划线、连字符、点
          .replace(/_+/g, '_')                    // 合并多个下划线
          .replace(/^_|_$/g, '')                  // 移除开头和结尾的下划线
          .trim() : '';
        
        console.log('🎬 [文件名清理] 清理后标题:', cleanTitle);
        console.log('🎬 [文件名清理] 清理后艺术家:', cleanArtist);
        
        // 确保清理后的文件名不为空
        if (cleanTitle && cleanTitle.length > 0) {
          if (cleanArtist && cleanArtist.length > 0) {
            fileName = `${cleanArtist} - ${cleanTitle}.mp4`;
          } else {
            fileName = `${cleanTitle}.mp4`;
          }
        } else {
          // 如果清理后文件名为空，使用时间戳
          const timestamp = Date.now();
          fileName = `video_${timestamp}.mp4`;
        }
      } else {
        // 如果元数据不可用，从URL中提取文件名
        const urlParts = sourceUrl.split('/');
        fileName = urlParts[urlParts.length - 1];
        
        // 如果URL包含查询参数，需要处理
        if (fileName.includes('?')) {
          fileName = fileName.split('?')[0];
        }
        
        // 检查是否是无效的文件名（如 download.aspx）
        if (fileName === 'download.aspx' || fileName.endsWith('.aspx') || fileName === 'stream') {
          // 使用时间戳生成文件名
          const timestamp = Date.now();
          fileName = `video_${timestamp}.mp4`;
        } else {
          // 确保文件名有正确的扩展名
          if (!fileName.includes('.')) {
            fileName += '.mp4';
          } else {
            // 如果已有扩展名但不是视频格式，替换为mp4
            const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv', '.m4v'];
            const hasVideoExtension = videoExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
            if (!hasVideoExtension) {
              const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
              fileName = `${nameWithoutExt}.mp4`;
            }
          }
        }
      }
      
      // 如果文件名仍然为空或无效，使用时间戳
      if (!fileName || fileName === '.mp4' || fileName === 'stream.mp4' || fileName === 'download.aspx' || fileName.endsWith('.aspx')) {
        const timestamp = Date.now();
        fileName = `video_${timestamp}.mp4`;
      }
      
      // 限制文件名长度（OneDrive 文件名限制为 255 字符，我们保守一点用 200）
      if (fileName.length > 200) {
        const nameWithoutExt = fileName.replace('.mp4', '');
        const truncatedName = nameWithoutExt.substring(0, 196); // 196 + .mp4 = 200
        fileName = `${truncatedName}.mp4`;
      }
      
      console.log('🎬 [流式传输] 最终文件名:', fileName);
      console.log('🎬 [流式传输] 文件名长度:', fileName.length);
      
      // 3. 执行流式传输
      const uploadResult = await this.streamTransferFile(
        sourceUrl,
        accessToken,
        musicFolderPath,
        fileName,
        onProgress
      );
      
      // 4. 下载文件并提取元数据
      const fileBlob = await this.downloadFileFromUrl(sourceUrl);
      const file = new File([fileBlob], fileName);
      const extractedMetadata = await this.extractAudioMetadata(file);
      
      // 5. 计算文件MD5值 - 将Blob转换为ArrayBuffer
      const fileBuffer = await fileBlob.arrayBuffer();
      const md5Hash = await this.calculateMD5(fileBuffer);
      
      // 6. 准备保存到服务器的数据（视频格式）
      const saveData = {
        musicFile: {
          fileId: uploadResult.id,
          fileName: fileName,
          fileSize: file.size,
          md5: md5Hash,
          mimeType: file.type || 'video/mp4',
          duration: extractedMetadata.duration,
          folderPath: musicFolderPath
        },
        metadata: {
          title: musicMetadata.title || extractedMetadata.title,
          artist: musicMetadata.artist || extractedMetadata.artist,
          album: musicMetadata.album || extractedMetadata.album || '抖音视频',
          genre: musicMetadata.genre || extractedMetadata.genre,
          releaseDate: musicMetadata.releaseDate,
          lyrics: this.normalizeLyrics(musicMetadata.lyrics)
        },
        // 添加封面和歌词信息
        coverUrl: musicMetadata.coverUrl || null,
        lyricsUrl: musicMetadata.lyricsUrl || null,
        extractedMetadata: extractedMetadata
      };
      
      // 6. 保存视频信息到服务器
      console.log('💾 [流式传输] 保存视频信息到服务器...');
      const saveResult = await this.saveMusicInfo(saveData);
      
      console.log('✅ [OneDrive API] 视频流式传输完成:', saveResult);
      return saveResult;
      
    } catch (error) {
      console.error('❌ [OneDrive API] 视频流式传输失败:', error);
      throw error;
    }
  },

  /**
   * 流式传输音乐文件：从分享链接下载并保存到用户OneDrive
   * 这是专门为音乐分享设计的功能
   */
  async streamTransferMusic(sourceUrl, musicMetadata = {}, onProgress = null) {
    try {
      console.log('🎵 [OneDrive API] 开始流式传输音乐文件');
      
      // 1. 获取目标用户的上传授权
      const authResponse = await this.getUploadAuth();
      const { accessToken, musicFolderPath } = authResponse;
      
      // 2. 生成唯一的文件名
      let fileName;
      
      // 优先使用音乐元数据生成文件名
      if (musicMetadata.title) {
        console.log('🎵 [文件名清理] 原始标题:', musicMetadata.title);
        console.log('🎵 [文件名清理] 原始艺术家:', musicMetadata.artist);
        
        // 清理文件名中的非法字符（包括更多特殊字符）
        const cleanTitle = musicMetadata.title
          .replace(/[<>:"/\\|?*#%&+=\s]+/g, '_')  // 替换特殊字符为下划线
          .replace(/[^\w\-_.]/g, '')              // 只保留字母、数字、下划线、连字符、点
          .replace(/_+/g, '_')                    // 合并多个下划线
          .replace(/^_|_$/g, '')                  // 移除开头和结尾的下划线
          .trim();
        
        const cleanArtist = musicMetadata.artist ? musicMetadata.artist
          .replace(/[<>:"/\\|?*#%&+=\s]+/g, '_')  // 替换特殊字符为下划线
          .replace(/[^\w\-_.]/g, '')              // 只保留字母、数字、下划线、连字符、点
          .replace(/_+/g, '_')                    // 合并多个下划线
          .replace(/^_|_$/g, '')                  // 移除开头和结尾的下划线
          .trim() : '';
        
        console.log('🎵 [文件名清理] 清理后标题:', cleanTitle);
        console.log('🎵 [文件名清理] 清理后艺术家:', cleanArtist);
        
        // 确保清理后的文件名不为空
        if (cleanTitle && cleanTitle.length > 0) {
          if (cleanArtist && cleanArtist.length > 0) {
            fileName = `${cleanArtist} - ${cleanTitle}.mp3`;
          } else {
            fileName = `${cleanTitle}.mp3`;
          }
        } else {
          // 如果清理后文件名为空，使用时间戳
          const timestamp = Date.now();
          fileName = `music_${timestamp}.mp3`;
        }
      } else {
        // 如果元数据不可用，从URL中提取文件名
        const urlParts = sourceUrl.split('/');
        fileName = urlParts[urlParts.length - 1];
        
        // 如果URL包含查询参数，需要处理
        if (fileName.includes('?')) {
          fileName = fileName.split('?')[0];
        }
        
        // 检查是否是无效的文件名（如 download.aspx）
        if (fileName === 'download.aspx' || fileName.endsWith('.aspx') || fileName === 'stream') {
          // 使用时间戳生成文件名
          const timestamp = Date.now();
          fileName = `music_${timestamp}.mp3`;
        } else {
          // 确保文件名有正确的扩展名
          if (!fileName.includes('.')) {
            fileName += '.mp3';
          }
        }
      }
      
      // 如果文件名仍然为空或无效，使用时间戳
      if (!fileName || fileName === '.mp3' || fileName === 'stream.mp3' || fileName === 'download.aspx' || fileName.endsWith('.aspx')) {
        const timestamp = Date.now();
        fileName = `music_${timestamp}.mp3`;
      }
      
      // 限制文件名长度（OneDrive 文件名限制为 255 字符，我们保守一点用 200）
      if (fileName.length > 200) {
        const nameWithoutExt = fileName.replace('.mp3', '');
        const truncatedName = nameWithoutExt.substring(0, 196); // 196 + .mp3 = 200
        fileName = `${truncatedName}.mp3`;
      }
      
      console.log('🎵 [流式传输] 最终文件名:', fileName);
      console.log('🎵 [流式传输] 文件名长度:', fileName.length);
      
      // 3. 执行流式传输
      const uploadResult = await this.streamTransferFile(
        sourceUrl,
        accessToken,
        musicFolderPath,
        fileName,
        onProgress
      );
      
      // 4. 下载文件并提取元数据
      const fileBlob = await this.downloadFileFromUrl(sourceUrl);
      const file = new File([fileBlob], fileName);
      const extractedMetadata = await this.extractAudioMetadata(file);
      
      // 5. 计算文件MD5值 - 将Blob转换为ArrayBuffer
      const fileBuffer = await fileBlob.arrayBuffer();
      const md5Hash = await this.calculateMD5(fileBuffer);
      
      // 6. 准备保存到服务器的数据
      const saveData = {
        musicFile: {
          fileId: uploadResult.id,
          fileName: fileName,
          fileSize: file.size,
          md5: md5Hash,
          mimeType: file.type,
          duration: extractedMetadata.duration,
          folderPath: musicFolderPath
        },
        metadata: {
          title: musicMetadata.title || extractedMetadata.title,
          artist: musicMetadata.artist || extractedMetadata.artist,
          album: musicMetadata.album || extractedMetadata.album,
          genre: musicMetadata.genre || extractedMetadata.genre,
          releaseDate: musicMetadata.releaseDate,
          lyrics: this.normalizeLyrics(musicMetadata.lyrics)
        },
        // 添加封面和歌词信息
        coverUrl: musicMetadata.coverUrl || null,
        lyricsUrl: musicMetadata.lyricsUrl || null,
        extractedMetadata: extractedMetadata
      };
      
      // 6. 保存音乐信息到服务器
      console.log('💾 [流式传输] 保存音乐信息到服务器...');
      const saveResult = await this.saveMusicInfo(saveData);
      
      console.log('✅ [OneDrive API] 音乐流式传输完成:', saveResult);
      return saveResult;
      
    } catch (error) {
      console.error('❌ [OneDrive API] 音乐流式传输失败:', error);
      throw error;
    }
  }
};

export default oneDriveAPI;
